import {
  EVENT_FILE_MAX_COUNT,
  EVENT_FILE_MAX_SIZE_BYTES,
  type EventFileFormItem,
} from '@/lib/types/event-files.types';
import type { EventFile } from '@/lib/types/database';

export function createEventFileClientId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(4);
    globalThis.crypto.getRandomValues(values);
    return Array.from(values, (value) => value.toString(16).padStart(8, '0'))
      .join('-')
      .slice(0, 36);
  }

  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

export function sanitizeEventFileTitle(filename: string): string {
  const nameWithoutExtension = filename.replace(/\.pdf$/i, '');
  const title = nameWithoutExtension
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return title || 'Event material';
}

export function sanitizeEventStorageFilename(filename: string): string {
  const base = filename
    .toLowerCase()
    .replace(/\.pdf$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

  return `${base || 'event-material'}.pdf`;
}

export function formatEventFileSize(sizeBytes: number): string {
  if (sizeBytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isPdfFile(file: File): boolean {
  return (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  );
}

export function validateEventFile(file: File): string | undefined {
  if (!isPdfFile(file)) {
    return 'Only PDF files can be uploaded.';
  }

  if (file.size > EVENT_FILE_MAX_SIZE_BYTES) {
    return 'PDFs must be 10 MB or smaller.';
  }

  if (file.size <= 0) {
    return 'This PDF is empty.';
  }

  return undefined;
}

export function getVisibleEventFileCount(files: EventFileFormItem[]): number {
  return files.filter((file) => !file.markedForDeletion).length;
}

export function validateEventFileItems(files: EventFileFormItem[]): string[] {
  const visibleFiles = files.filter((file) => !file.markedForDeletion);
  const errors: string[] = [];

  if (visibleFiles.length > EVENT_FILE_MAX_COUNT) {
    errors.push(`Events can have up to ${EVENT_FILE_MAX_COUNT} PDFs.`);
  }

  visibleFiles.forEach((file) => {
    if (!file.displayTitle.trim()) {
      errors.push(`${file.originalFilename} needs a title.`);
    }

    if (file.error) {
      errors.push(`${file.originalFilename}: ${file.error}`);
    }

    if (file.targetOccurrenceIndexes.length === 0) {
      errors.push(`${file.originalFilename} must target at least one event.`);
    }
  });

  return errors;
}

export function eventFileToFormItem(file: EventFile): EventFileFormItem {
  return {
    id: file.id,
    kind: 'existing',
    displayTitle: file.display_title,
    originalFilename: file.original_filename,
    sizeBytes: file.size_bytes,
    targetOccurrenceIndexes: [0],
    storagePath: file.storage_path,
  };
}

export function getDefaultOccurrenceIndexes(recurrenceCount: number): number[] {
  return Array.from(
    { length: Math.max(1, recurrenceCount) },
    (_, index) => index
  );
}
